import { getStoryCategoryData } from "@/utils/storyCategorization";
import { getStorySection } from "@/utils/storySections";

const includesAny = (haystack, keywords) => keywords.some((keyword) => haystack.includes(keyword));

const getHaystack = (story) => `${story?.headline || ""} ${story?.excerpt || ""} ${story?.caption || ""} ${story?.location || ""} ${story?.ai_story || ""} ${(story?.tags || []).join(" ")}`.toLowerCase();

export const TOPIC_PAGE_CONFIG = {
  crime: {
    key: "crime",
    route: "/houston-crime-news",
    title: "Houston Crime News",
    eyebrow: "Houston search desk",
    description: "Track Houston crime news, police investigations, shootings, robberies, standoffs, and fast-moving public safety updates from Content Watch HQ.",
    heroCopy: "This page is tuned for people searching Houston crime news, police activity, and public safety updates across the city.",
    searchTargets: ["Houston crime news", "Houston police activity", "Houston shootings", "Houston robbery news"],
    matches: (story) => {
      const section = getStorySection(story);
      const { sceneCategory } = getStoryCategoryData(story);
      return section === "breaking" && [
        "Officer-Involved Shootings",
        "Road Rage Shootings",
        "Shootings",
        "Homicide Investigations",
        "SWAT & Standoffs",
        "Armed Robberies",
        "Robberies",
        "Stabbings",
        "Police Activity",
        "School Threats",
        "Missing & Endangered Persons",
      ].includes(sceneCategory);
    },
  },
  traffic: {
    key: "traffic",
    route: "/houston-traffic-news",
    title: "Houston Traffic News",
    eyebrow: "Houston search desk",
    description: "Follow Houston traffic news, freeway crashes, road rage incidents, 18-wheeler wrecks, police chases, and major roadway disruptions from Content Watch HQ.",
    heroCopy: "Built for Houston traffic news searches, this page groups the latest crashes, pursuits, and freeway trouble spots into one feed.",
    searchTargets: ["Houston traffic news", "Houston crash news", "Houston freeway accident", "Houston road closure"],
    matches: (story) => {
      const haystack = getHaystack(story);
      const section = getStorySection(story);
      const { sceneCategory } = getStoryCategoryData(story);
      return section === "breaking" && ([
        "Pedestrian Crashes",
        "Hit-and-Runs",
        "Motorcycle Crashes",
        "18-Wheeler Crashes",
        "Deadly Car Crashes in Houston",
        "Car Crashes",
        "Police Chases",
      ].includes(sceneCategory) || includesAny(haystack, ["traffic", "freeway", "highway", "road closure", "lane closure"]));
    },
  },
  weather: {
    key: "weather",
    route: "/houston-weather-news",
    title: "Houston Weather News",
    eyebrow: "Houston search desk",
    description: "Stay on top of Houston weather news, flood alerts, storm damage, severe rain coverage, and emergency weather updates from Content Watch HQ.",
    heroCopy: "Houston weather searches need timely flood, storm, and severe weather updates. This page keeps those stories grouped together.",
    searchTargets: ["Houston weather news", "Houston flood news", "Houston storm damage", "Houston weather alerts"],
    matches: (story) => {
      const section = getStorySection(story);
      const { sceneCategory } = getStoryCategoryData(story);
      return section === "breaking" && ["Flooding", "Storm Damage", "Weather Alerts", "Hazmat Incidents"].includes(sceneCategory);
    },
  },
  sports: {
    key: "sports",
    route: "/houston-sports-news",
    title: "Houston Sports News",
    eyebrow: "Houston search desk",
    description: "Get Houston sports news, game-day coverage, high school highlights, local team updates, and office-published sports stories from Content Watch HQ.",
    heroCopy: "This Houston sports news page gives search engines and readers a dedicated local route for sports coverage beyond the general sports tab.",
    searchTargets: ["Houston sports news", "Houston high school sports", "Houston team updates", "Houston game coverage"],
    matches: (story) => getStorySection(story) === "sports",
  },
  neighborhoods: {
    key: "neighborhoods",
    route: "/houston-neighborhood-news",
    title: "Houston Neighborhood News",
    eyebrow: "Houston search desk",
    description: "Explore Houston neighborhood news, community issues, housing stories, investigations, and what’s new across local districts with Content Watch HQ.",
    heroCopy: "For neighborhood-focused searches, this page highlights community updates, housing issues, and local Houston stories from the What’s New desk.",
    searchTargets: ["Houston neighborhood news", "Houston community news", "Houston housing issues", "Houston local updates"],
    matches: (story) => {
      const haystack = getHaystack(story);
      const section = getStorySection(story);
      const { sceneCategory } = getStoryCategoryData(story);
      return section === "editorial" || ["Slumlords & Housing", "Corrupt Businesses", "What’s New"].includes(sceneCategory) || includesAny(haystack, ["community", "neighborhood", "apartment", "housing", "district"]);
    },
  },
};

export const TOPIC_PAGE_LIST = Object.values(TOPIC_PAGE_CONFIG);

export const getTopicStories = (feed, topicKey) => {
  const topic = TOPIC_PAGE_CONFIG[topicKey];
  if (!topic) {
    return [];
  }
  return (feed || []).filter((story) => topic.matches(story));
};